import { Product } from '@/types';
import { getModelDisplayConfig } from '@/config/model-display-configs';
import CopyButton from '@/components/ui/copy-button';
import { cn } from '@/lib/utils';
import { ProductAttributeErrorBoundary } from './product-attribute-error-boundary';
import { useInSuccessfulOrders } from '@/lib/hooks/use-in-orders';
import { Button } from '@nextui-org/react';
import { DownloadIcon } from 'lucide-react';
import client from '@/data/client';
import { useMutation } from 'react-query';

interface ProductAttributesProps {
  product: Product;
  className?: string;
}

// Helper function to format the display value
const formatDisplayValue = (value: any): string | boolean | number => {
  if (value === null || value === undefined) return '';

  // Handle boolean values
  if (typeof value === 'boolean') return value;

  // Handle objects with label and value properties
  if (typeof value === 'object' && value !== null) {
    if ('label' in value && 'value' in value) {
      return value.label;
    }
    // For other objects, try to stringify them
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return value;
};

const AttributeItem = ({ config, value }: { config: any; value: any }) => {
  try {
    // Special handling for input_image
    if (config.name === 'input_image' && value?.original) {
      return (
        <div className="rounded-lg border border-light-400 bg-light-200 p-4 dark:border-dark-400 dark:bg-dark-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-dark dark:text-light">
                {config.label || 'Input Image'}
              </h3>
              {config.description && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {config.description}
                </p>
              )}
            </div>
          </div>
          <div className="mt-4">
            <img
              src={value.original}
              alt="Input image"
              className="max-h-[200px] w-auto rounded-lg object-contain"
              loading="lazy"
            />
          </div>
        </div>
      );
    }

    // Special handling for prompt examples
    if (config.name === 'prompt_example' && Array.isArray(value)) {
      return (
        <div>
          {value.map((example, index) => (
            <div
              key={`${config.name}-${index}`}
              className="mb-4 rounded-lg border border-light-400 bg-light-200 p-4 dark:border-dark-400 dark:bg-dark-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-dark dark:text-light">
                    Prompt Example {index + 1}
                  </h3>
                  {config.description && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {config.description}
                    </p>
                  )}
                </div>
                {config.copyable && (
                  <CopyButton
                    value={example.text ?? ''}
                    className="ml-2 shrink-0"
                  />
                )}
              </div>
              <div className="mt-2 break-words text-sm text-dark-600 dark:text-light-800">
                <div className="font-mono">{example.text}</div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Format the value using the helper function first
    const formattedValue = formatDisplayValue(value);
    // Then apply any config-specific formatting
    const displayValue = config.format
      ? (() => {
          try {
            return config.format(formattedValue);
          } catch (e) {
            console.error(`Error formatting value for ${config.name}:`, e);
            return formattedValue;
          }
        })()
      : formattedValue;

    return (
      <div className="rounded-lg border border-light-400 bg-light-200 p-4 dark:border-dark-400 dark:bg-dark-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-dark dark:text-light">
              {config.label}
            </h3>
            {config.description && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {config.description}
              </p>
            )}
          </div>
          {config.copyable && (
            <CopyButton
              value={String(displayValue)}
              className="ml-2 shrink-0"
            />
          )}
        </div>
        <div className="mt-2 break-words text-sm text-dark-600 dark:text-light-800">
          {typeof displayValue === 'string' && displayValue.includes('[') ? (
            <div className="font-mono">
              {displayValue.split(/(\[.*?\])/g).map((part, index) => {
                if (part.startsWith('[') && part.endsWith(']')) {
                  return (
                    <span key={index} className="text-brand">
                      {part}
                    </span>
                  );
                }
                return part;
              })}
            </div>
          ) : typeof displayValue === 'boolean' ? (
            <div className="font-mono">{displayValue ? 'Yes' : 'No'}</div>
          ) : (
            displayValue
          )}
        </div>
      </div>
    );
  } catch (error) {
    console.error(`Error rendering attribute ${config.name}:`, error);
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/50">
        <p className="text-sm text-red-600 dark:text-red-400">
          Unable to display this attribute. Please contact support:{' '}
          <a href="mailto:info@promptbase.com">info@promptbase.com</a>.
        </p>
      </div>
    );
  }
};

export default function ProductAttributes({
  product,
  className,
}: ProductAttributesProps) {
  const { file } = useInSuccessfulOrders(
    parseInt(product?.id ?? '0', 10)
  );
  const { mutate } = useMutation(client.orders.generateDownloadLink, {
    onSuccess: (data) => {
      function download(fileUrl: string, fileName: string) {
        let a = document.createElement('a');
        a.href = fileUrl;
        a.setAttribute('download', fileName);
        a.click();
      }

      download(data, product.name);
    },
  });

  try {
    const displayConfig = getModelDisplayConfig(product.ai_model?.slug ?? '');

    // Parse hidden_fields if it exists
    let hiddenFields: Record<string, any> = {};
    try {
      if (typeof product.hidden_fields === 'string') {
        hiddenFields = JSON.parse(product.hidden_fields);
      } else if (
        product.hidden_fields &&
        typeof product.hidden_fields === 'object'
      ) {
        hiddenFields = product.hidden_fields;
      }
    } catch (e) {
      console.error('Failed to parse hidden_fields:', e);
    }

    // Create a map of configured fields for quick lookup
    const configuredFields = new Map(
      displayConfig.map((config) => [config.name, config])
    );

    // Get all hidden field keys and create configs for them
    const hiddenFieldConfigs = Object.keys(hiddenFields).map((key) => {
      const existingConfig = displayConfig.find(
        (config) => config.name === `hidden_fields.${key}`
      );
      if (existingConfig) {
        return existingConfig;
      }
      return {
        name: `hidden_fields.${key}`,
        label: key
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        order: 1000,
        copyable: false,
      };
    });

    // Combine and sort all configs
    const allConfigs = [
      ...displayConfig,
      ...hiddenFieldConfigs.filter(
        (config) => !configuredFields.has(config.name)
      ),
    ].sort((a, b) => (a.order || 1000) - (b.order || 1000));

    // Update full-width fields array to remove prompt_template
    const fullWidthFields = ['tips', 'prompt_example'];
    const gridFields = allConfigs.filter(
      (config) => !fullWidthFields.includes(config.name)
    );
    const fullWidthConfigs = allConfigs.filter((config) =>
      fullWidthFields.includes(config.name)
    );

    // Get input image and template configs
    const inputImageConfig = allConfigs.find(
      (config) => config.name === 'input_image'
    );
    const templateConfig = allConfigs.find(
      (config) => config.name === 'prompt_template'
    );

    if (product?.prompt_template?.length < 1)
      return (
        <div className="mt-4 flex flex-col xs:gap-2.5 xs:pb-4 md:flex-row md:items-center md:gap-3.5 lg:gap-4 3xl:p-14">
          {/* HACK: 69696 is a dummy attachment_id */}
          {file?.digital_file_id && file?.file.attachment_id !== 69696 && (
            <Button
              onClick={() => mutate(file.digital_file_id)}
              disableRipple
              className="mt-2.5 w-full flex-1 bg-brand text-white xs:mt-0"
            >
              <DownloadIcon className="h-auto w-4" />
              {/* {t('text-download')} */}
              Download Files
            </Button>
          )}
        </div>
      );

    return (
      <div className={cn('my-4 space-y-6', className)}>
        {/* Input Image and Template Section */}
        <div className="flex flex-col gap-4 lg:flex-row">
          {inputImageConfig && product.input_image && (
            <ProductAttributeErrorBoundary>
              <div className="w-full shrink-0 lg:w-[200px]">
                <AttributeItem
                  config={inputImageConfig}
                  value={product.input_image}
                />
              </div>
            </ProductAttributeErrorBoundary>
          )}
          {templateConfig && product.prompt_template && (
            <ProductAttributeErrorBoundary>
              <div className="flex-1">
                <AttributeItem
                  config={templateConfig}
                  value={product.prompt_template}
                />
              </div>
            </ProductAttributeErrorBoundary>
          )}
        </div>

        {/* Grid layout for dense fields */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {gridFields.map((config) => {
            // Skip input_image and prompt_template as they're handled separately
            if (
              config.name === 'input_image' ||
              config.name === 'prompt_template'
            ) {
              return null;
            }

            let value;
            try {
              if (config.name.startsWith('hidden_fields.')) {
                const fieldKey = config.name.replace('hidden_fields.', '');
                value = hiddenFields[fieldKey];
              } else {
                value = product[config.name as keyof Product];
              }

              if (value === null || value === undefined) return null;

              return (
                <ProductAttributeErrorBoundary key={config.name}>
                  <AttributeItem config={config} value={value} />
                </ProductAttributeErrorBoundary>
              );
            } catch (error) {
              console.error(
                `Error processing attribute ${config.name}:`,
                error
              );
              return null;
            }
          })}
        </div>

        {/* Full-width fields */}
        <div className="space-y-4">
          {fullWidthConfigs.map((config) => {
            let value;
            try {
              if (config.name.startsWith('hidden_fields.')) {
                const fieldKey = config.name.replace('hidden_fields.', '');
                value = hiddenFields[fieldKey];
              } else {
                value = product[config.name as keyof Product];
              }

              if (value === null || value === undefined) return null;

              return (
                <ProductAttributeErrorBoundary key={config.name}>
                  <AttributeItem config={config} value={value} />
                </ProductAttributeErrorBoundary>
              );
            } catch (error) {
              console.error(
                `Error processing attribute ${config.name}:`,
                error
              );
              return null;
            }
          })}
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error in ProductAttributes:', error);
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/50">
        <p className="text-sm text-red-600 dark:text-red-400">
          Unable to display product attributes.
        </p>
      </div>
    );
  }
}
